import { useState, useRef, useCallback } from 'react'
import MessageDialog from '../../shared/ui/MessageDialog.jsx';

/**
 * Hook for managing voice recording functionality
 * Uses MediaRecorder API to capture audio in WebM/Opus format
 */
export const useVoiceRecording = () => {
  const [isRecording, setIsRecording] = useState(false)
  const [audioBlob, setAudioBlob] = useState(null)
  const [audioUrl, setAudioUrl] = useState(null)
  const [error, setError] = useState(null)
  const [recordingTime, setRecordingTime] = useState(0)

  const mediaRecorderRef = useRef(null)
  const streamRef = useRef(null)
  const chunksRef = useRef([])
  const timerRef = useRef(null)

  /**
   * Start recording audio
   * @returns {Promise<boolean>} Returns true if recording started successfully, false otherwise
   */
  const startRecording = useCallback(async () => {
    try {
      setError(null)
      setAudioBlob(null)
      setAudioUrl(null)
      chunksRef.current = []
      setRecordingTime(0)

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('お使いのブラウザは音声録音をサポートしていません。HTTPS接続で最新のブラウザをご利用ください。')
      }

      // Request microphone access with timeout detection
      const getUserMediaPromise = navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 48000,
          echoCancellation: true,
          noiseSuppression: true
        }
      })

      // Log if permission is taking too long (user might not see the prompt)
      const timeoutId = setTimeout(() => {
        console.warn('[VoiceRecording] Permission prompt may be waiting for your response. Please check for a microphone permission dialog in your browser (usually near the address bar).')
      }, 3000)

      const stream = await getUserMediaPromise
      clearTimeout(timeoutId)
      streamRef.current = stream

      // Create MediaRecorder with WebM/Opus format
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4'


      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000
      })
      mediaRecorderRef.current = mediaRecorder

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType })
        setAudioBlob(blob)
        setAudioUrl(URL.createObjectURL(blob))
      }

      mediaRecorder.onerror = (event) => {
        console.error('[VoiceRecording] MediaRecorder error:', event.error)
        MessageDialog.show('メッセージ', 'DEBUG MediaRecorder ERROR: ' + (event.error?.message || 'Unknown error'))
      }

      mediaRecorder.start(100) // Collect data every 100ms
      
      setIsRecording(true)

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1)
      }, 1000)

      return true // Recording started successfully

    } catch (err) {
      console.error('[VoiceRecording] Failed to start recording:', err)
      console.error('[VoiceRecording] Error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack
      })
      MessageDialog.show('メッセージ', 'DEBUG ERROR: ' + (err.message || err.name || 'Unknown error'))
      // Provide more specific error messages
      let errorMessage = 'マイクへのアクセスに失敗しました'
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'マイクへのアクセスが拒否されました。ブラウザの設定でマイクへのアクセスを許可してください。'
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = 'マイクが見つかりません。マイクが接続されているか確認してください。'
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage = 'マイクにアクセスできません。他のアプリケーションがマイクを使用している可能性があります。'
      } else if (err.message) {
        errorMessage = err.message
      }
      setError(errorMessage)
      setIsRecording(false)
      return false // Recording failed
    }
  }, [])

  /**
   * Stop recording audio
   */
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)

      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }

      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
        streamRef.current = null
      }
    }
  }, [isRecording])

  /**
   * Cancel recording and clean up
   */
  const cancelRecording = useCallback(() => {
    stopRecording()
    setAudioBlob(null)
    setAudioUrl(null)
    setRecordingTime(0)
    chunksRef.current = []
  }, [stopRecording])

  /**
   * Get audio data as Base64 string
   * @returns {Promise<string>} Base64 encoded audio data
   */
  const getAudioAsBase64 = useCallback(async () => {
    if (!audioBlob) {
      throw new Error('No audio recorded')
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        // Remove data URL prefix to get raw Base64
        const base64 = reader.result.split(',')[1]
        resolve(base64)
      }
      reader.onerror = reject
      reader.readAsDataURL(audioBlob)
    })
  }, [audioBlob])

  /**
   * Format recording time as MM:SS
   */
  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }, [])

  return {
    isRecording,
    audioBlob,
    audioUrl,
    error,
    recordingTime,
    formattedTime: formatTime(recordingTime),
    startRecording,
    stopRecording,
    cancelRecording,
    getAudioAsBase64
  }
}

export default useVoiceRecording
